import React, { useEffect, useState } from "react";
import Button from "../ui/Button";

interface PaginationProps {
  page: number;
  totalPages: number;
  setPage: (page: number) => void;
  totalItems?: number;
  limit?: number;
}

// Above this many pages, clicking through numbered buttons isn't practical - show a
// "go to page" input instead.
const JUMP_TO_PAGE_THRESHOLD = 20;

const Pagination: React.FC<PaginationProps> = ({ page, totalPages, setPage, totalItems, limit }) => {
  const [jumpValue, setJumpValue] = useState(String(page));

  useEffect(() => {
    setJumpValue(String(page));
  }, [page]);

  // Nothing to show at all - no rows means no count line and no page buttons.
  // A single page still renders: the count line orients the user ("1 to 6 of 6")
  // and the lone page button shows where they are.
  if (totalPages < 1 || totalItems === 0) return null;

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2
  );

  const commitJump = () => {
    const parsed = parseInt(jumpValue, 10);
    if (!isNaN(parsed)) {
      const clamped = Math.min(Math.max(parsed, 1), totalPages);
      setPage(clamped);
      setJumpValue(String(clamped));
    } else {
      setJumpValue(String(page));
    }
  };

  return (
    <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
      {totalItems != null && limit != null ? (
        <div className="text-sm text-neutral-600">
          Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalItems)} of {totalItems}
        </div>
      ) : (
        <div />
      )}
      <div className="flex items-center gap-2">
        {totalPages > JUMP_TO_PAGE_THRESHOLD && (
          <div className="flex items-center gap-1 mr-2 text-sm text-neutral-600">
            <label htmlFor="pagination-jump-input">Go to page</label>
            <input
              id="pagination-jump-input"
              type="number"
              min={1}
              max={totalPages}
              value={jumpValue}
              onChange={(e) => setJumpValue(e.target.value)}
              onBlur={commitJump}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitJump();
                }
              }}
              className="w-16 px-2 py-1 text-sm bg-white border rounded-md border-neutral-300 focus:outline-none focus:border-primary-500"
            />
            <span>of {totalPages}</span>
          </div>
        )}
        <Button
          variant="neutral"
          size="sm"
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
        >
          Previous
        </Button>
        {pageNumbers.map((p, index, array) => {
          const showEllipsis = index > 0 && array[index - 1] !== p - 1;
          return (
            <React.Fragment key={p}>
              {showEllipsis && (
                <span className="px-2 py-1 text-sm text-neutral-400">...</span>
              )}
              <button
                onClick={() => setPage(p)}
                disabled={page === p}
                aria-current={page === p ? "page" : undefined}
                className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                  page === p
                    ? "bg-primary-700 text-white border-primary-700 cursor-default font-medium"
                    : "bg-white border-primary-300 text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
                }`}
              >
                {p}
              </button>
            </React.Fragment>
          );
        })}
        <Button
          variant="neutral"
          size="sm"
          onClick={() => setPage(page + 1)}
          disabled={page === totalPages}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
